import * as moment from 'moment';
import {
    IDischargeMeasurementPair,
    IImomoResponse,
    IDataValue,
    IDailyTriplet,
    IDischargeModel, IJournalData, IBulletinData, IBulkData, IDischargeNorm, IPrecipitationNorm, ITemperatureNorm
} from './api.types';
import {IIcePhenomena} from '../operational/input/kn15-telegram/kn15-parse.service';
import {ITemperatures} from '../operational/input/kn15-telegram/kn15-parse.service';

export interface IDailyDataPart {
  waterLevel:number;
  discharge:number;
}

export interface IDailyDataRequest {
  date:moment.Moment,
  morningData?:IDailyDataPart;
  eveningData?:IDailyDataPart;
  averageData?:IDailyDataPart;
}

export interface IDailyDataParams {
  dischargeData?: {
    date?: moment.Moment,
    siteId: number,
    dailyData?:IDailyDataRequest[],
    temperatures?:ITemperatures,
    icePhenomenae?:Array<IIcePhenomena>,
    dischargeModelParams?:{
      dischargeModel:IDischargeModel,
      createdDischargeModel:boolean,
    },
    dischargeMeasurementData?:{
      waterLevel:number,
      discharge:number,
      freeRiverArea?:number,
      maximumDepth?:number,
      date?: moment.Moment | number
    }
  },
  meteoData?: {
    siteId?: number,
    month:number,
    decade: number,
    precipitation:number;
    precipitationChecksum:number,
    airTemp: number;
    year: number,
    currentDate: moment.Moment
  }
}

export interface IMeteoDataParams {
  meteoData: {
    siteId?: number;
    month:number,
    decade: number,
    precipitation:number;
    precipitationChecksum:number;
    airTemp: number;
  }
}

export interface IDataValueEditRequest {
  id:number;
  dataValue:number;
}

export interface IDataValueApi {
  getDischargePairs(stationId:number,
                    year:number):ng.IPromise<IDischargeMeasurementPair[]>;
  getMaximumDischarge(stationId:number):ng.IPromise<IDataValue>;
  getYearDailyAverageData(stationId:number, year:number, dataType: string): ng.IPromise<IDataValue[]>;
  getDailyData(stationId:number, startDate:moment.Moment,
               endDate:moment.Moment):ng.IPromise<IDailyTriplet[]>;
  editDailyData(stationId:number, date:moment.Moment,
                waterLevelMorning:number|null, waterLevelEvening:number|null,
                waterLevelAverage:number|null):ng.IPromise<IDailyTriplet>
  storeDailyData(params:IDailyDataParams):ng.IPromise<void>;
  checkDailyData(params:IDailyDataParams):ng.IPromise<IDailyDataParams>;
  getJournalData(month:number, year:number, stationIds:number[]):ng.IPromise<IJournalData>;
  editDischargeData(stationId:number, waterLevel:IDataValueEditRequest,
                    discharge:IDataValueEditRequest,
                    freeRiverArea?:IDataValueEditRequest):ng.IPromise<void>;
  editMeteoDecadalData(stationId:number, year: number, month: number, decadeInMonth: number, temperature: number, precipitation: number):ng.IPromise<void>;
  deleteDischargeData(stationId:number, waterLevelId:number,
                      dischargeId:number):ng.IPromise<void>;
  getBulletin(stationIds:number[], date:moment.Moment):ng.IPromise<IBulletinData>;
  getStationNorm(stationId:number, dataType: string):ng.IPromise<IDischargeNorm>;
  getBulkData(stationIds:number[]):ng.IPromise<IBulkData>;
}

export default class DataValueApi implements IDataValueApi {
  static $inject = ['backendUrl', '$http'];

  private endpoint:string;

  constructor(backendUrl:string, private $http:ng.IHttpService) {
    this.endpoint = `${backendUrl}/data_values`;
  }

  getDischargePairs(stationId:number, year:number) {
    return this.$http<IImomoResponse<IDischargeMeasurementPair>>({
      url: `${this.endpoint}/discharge`,
      method: 'GET',
      params: {
        year: year,
        hydroYear: false,
        siteId: stationId
      }
    }).then(httpData => httpData.data.resources);
  }

  getMaximumDischarge(stationId:number) {
    return this.$http<IImomoResponse<IDataValue>>({
      url: `${this.endpoint}/safe_discharge`,
      method: 'GET',
      params: {
        siteId: stationId
      }
    }).then(httpData => httpData.data.resources[0]);
  }

  getYearDailyAverageData(stationId:number, year:number, dataType: string) {
    return this.$http<IImomoResponse<IDataValue>>({
      url: `${this.endpoint}/year_average_data`,
      method: 'GET',
      params: {
        year: year,
        hydroYear: false,
        siteId: stationId,
        dataType: dataType
      }
    }).then(httpData => httpData.data.resources);
  }

  getDailyData(stationId:number, startDate:moment.Moment,
               endDate:moment.Moment) {
    return this.$http<IImomoResponse<IDailyTriplet>>({
      url: `${this.endpoint}/daily_data`,
      method: 'GET',
      params: {
        siteId: stationId,
        startDate: startDate.format(),
        endDate: endDate.format()
      }
    }).then(httpData => httpData.data.resources);
  }

  editDailyData(stationId:number, date:moment.Moment,
                waterLevelMorning:number|null, waterLevelEvening:number|null,
                waterLevelAverage:number|null):ng.IPromise<IDailyTriplet> {
    return this.$http<IImomoResponse<IDailyTriplet>>({
      url: `${this.endpoint}/daily_data`,
      method: 'PUT',
      data: {
        siteId: stationId,
        date: date,
        waterLevelMorning: waterLevelMorning,
        waterLevelEvening: waterLevelEvening,
        waterLevelAverage: waterLevelAverage,
        sourceId: 1
      }
    }).then(httpData => httpData.data.resources[0]);
  }

  storeDailyData({
      dischargeData,
      meteoData
  }:IDailyDataParams) {
    let dischargeModel:any =
        dischargeData && dischargeData.dischargeModelParams && dischargeData.dischargeModelParams.createdDischargeModel
            ? dischargeData.dischargeModelParams.dischargeModel
            : null;
    if (dischargeModel){
      dischargeModel = angular.copy(dischargeModel);
      dischargeModel.validFrom = moment.unix(dischargeModel.validFrom);
    }
    return this.$http({
      url: `${this.endpoint}/daily_data_new`,
      method: 'POST',
      data: {
        dischargeData,
        meteoData
      }
    }).then(httpData => console.log());
  }

  checkDailyData({
      dischargeData,
      meteoData
  }:IDailyDataParams) {
    let dischargeModel:any =
        dischargeData && dischargeData.dischargeModelParams && dischargeData.dischargeModelParams.createdDischargeModel
            ? dischargeData.dischargeModelParams.dischargeModel
            : null;
    if (dischargeModel){
      dischargeModel = angular.copy(dischargeModel);
      dischargeModel.validFrom = moment.unix(dischargeModel.validFrom);
    }
    return this.$http<IImomoResponse<IDailyDataParams>>({
      url: `${this.endpoint}/check_daily_data_new`,
      method: 'POST',
      data: {
        dischargeData,
        meteoData
      }
    }).then(httpData => httpData.data.resources[0]);
  }

  getJournalData(month:number, year:number, stationIds:number[]) {
    return this.$http<IImomoResponse<IJournalData>>({
      url: `${this.endpoint}/journal`,
      method: 'GET',
      params: {
        month: month + 1,
        year,
        siteIds: stationIds
      }
    }).then(httpData => httpData.data.resources[0]);
  }

  editDischargeData(stationId:number, waterLevel:IDataValueEditRequest,
                    discharge:IDataValueEditRequest,
                    freeRiverArea:IDataValueEditRequest) {
    return this.$http({
      url: `${this.endpoint}/discharge_data`,
      method: 'PUT',
      data: {
        waterLevel, discharge, freeRiverArea
      }
    }).then(() => {
    });
  }

  editMeteoDecadalData(
    stationId:number,
    year: number,
    month: number,
    decadeInMonth: number,
    temperature: number,
    precipitation: number
  ) {
    return this.$http<IImomoResponse<IDataValueEditRequest>>({
      url: `${this.endpoint}/edit_meteo_site_data_value`,
      method: 'POST',
      data: { 
          siteId: stationId,
          year,
          month,
          decadeInMonth,
          temperature,
          precipitation
      }
    }).then(() => {});
  }

  deleteDischargeData(stationId:number, waterLevelId:number,
                      dischargeId:number) {
    return this.$http({
      url: `${this.endpoint}/discharge_data`,
      method: 'DELETE',
      params: {
        siteId: stationId,
        waterLevelId, dischargeId
      }
    }).then(() => {
    });
  }

  getBulletin(stationIds:number[], date:moment.Moment):ng.IPromise<IBulletinData> {
    return this.$http<IImomoResponse<IBulletinData>>({
      url: `${this.endpoint}/bulletin`,
      method: 'GET',
      params: {
        siteIds: stationIds,
        year: date.year(),
        month: date.month() + 1,
        day: date.date()
      }
    }).then(httpData => httpData.data.resources[0]);
  }

  getStationNorm(stationId:number, dataType: string):ng.IPromise<IDischargeNorm | IPrecipitationNorm | ITemperatureNorm> {
    return this.$http<IImomoResponse<IDischargeNorm | IPrecipitationNorm | ITemperatureNorm>>({
      url: `${this.endpoint}/norm`,
      method: 'GET',
      params: {
        siteId: stationId,
        dataType: dataType
      }
    }).then(httpData => {
      return httpData.data.resources[0];
    });
  }

  getBulkData(stationIds:number[]):ng.IPromise<IBulkData> {
    return this.$http<IImomoResponse<IBulkData>>({
      url: `${this.endpoint}/bulk_download`,
      method: 'POST',
      data: {
        siteIds: stationIds
      }
    }).then(httpData => httpData.data.resources[0]);
  }
}



/** WEBPACK FOOTER **
 ** ./app/api/data-value-api.service.ts
 **/